/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Mic, MicOff, Volume2 } from "lucide-react";
import { API_BASE } from "../../lib/api";
import { TEST_IDS } from "../../constants/testIds";

const AGENTS = [
  { key: "investment_banker", label: "Investment Banker" },
  { key: "investor_relations", label: "Investor Relations" },
  { key: "due_diligence", label: "Due Diligence" },
  { key: "market_research", label: "Market Research" },
  { key: "esg_impact", label: "ESG & Impact" },
  { key: "tokenization", label: "Tokenization" },
  { key: "scheduling", label: "Scheduling" },
];

const LANGS = [
  // Primary — US, Shanghai, Singapore audience
  { code: "en-US", label: "English", short: "EN" },
  { code: "zh-CN", label: "中文 (简体 / 普通话)", short: "中文" },
  { code: "en-SG", label: "English (Singapore)", short: "EN-SG" },
  // Additional coverage
  { code: "zh-HK", label: "中文 (繁體)", short: "繁中" },
  { code: "yue-Hant-HK", label: "廣東話", short: "粵" },
  { code: "ar-SA", label: "العربية", short: "AR" },
  { code: "fr-FR", label: "Français", short: "FR" },
  { code: "es-ES", label: "Español", short: "ES" },
  { code: "pt-BR", label: "Português", short: "PT" },
  { code: "ja-JP", label: "日本語", short: "日" },
  { code: "ko-KR", label: "한국어", short: "한" },
  { code: "hi-IN", label: "हिन्दी", short: "HI" },
];

// Quick-toggle pills — one-click access for the three primary audiences
const QUICK_LANGS = [
  { code: "en-US", label: "English" },
  { code: "zh-CN", label: "中文" },
];

// Detect visitor's preferred language on first mount
function detectDefaultLang() {
  if (typeof navigator === "undefined") return "en-US";
  const primary = (navigator.language || "en").toLowerCase();
  const all = (navigator.languages || [primary]).map(l => l.toLowerCase());
  const isChinese = all.some(l => l.startsWith("zh") || l.startsWith("yue"));
  if (isChinese) return "zh-CN";
  return "en-US";
}

// Bilingual welcome tuned to the selected language
function welcomeMessage(langCode) {
  if (langCode.startsWith("zh") || langCode.startsWith("yue")) {
    return "欢迎来到 First Providence Capital。我是您的 AI 投资银行家,可以为您介绍我们在能源、基础设施、科技和实物资产领域的战略,并回答您的尽职调查问题。请随时提问。";
  }
  return "Welcome to First Providence Capital. I'm the AI Investment Banker. Ask me about our strategies across energy, infrastructure, technology, and real assets — or switch to a specialized agent above.";
}

export default function AIWidget() {
  const [open, setOpen] = useState(false);
  const [agent, setAgent] = useState("investment_banker");
  const [lang, setLang] = useState(detectDefaultLang());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    if (!open && messages.length === 0) {
      setMessages([{ role: "assistant", content: welcomeMessage(lang) }]);
    }
  }, [open, lang]);

  // Swap the greeting when the user changes language before sending any messages
  const switchLang = (newLang) => {
    setLang(newLang);
    setMessages(prev => {
      // If the conversation only has the initial assistant greeting, replace it in the new language
      if (prev.length === 1 && prev[0].role === "assistant") {
        return [{ role: "assistant", content: welcomeMessage(newLang) }];
      }
      return prev;
    });
  };

  const speak = (text) => {
    if (!voiceOn || !text) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      u.rate = 1.02;
      u.pitch = 1.0;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    const nextMessages = [...messages, { role: "user", content: msg }, { role: "assistant", content: "" }];
    setMessages(nextMessages);

    try {
      const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, agent, message: msg, language: lang }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let capturedSession = sessionId;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n\n")) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          if (trimmed.startsWith("event: done")) {
            const idLine = trimmed.split("\n").find(l => l.startsWith("data:"));
            if (idLine) capturedSession = idLine.replace("data:", "").trim();
            continue;
          }
          if (trimmed.startsWith("data:")) {
            const piece = trimmed.replace(/^data:\s?/, "");
            assistantText += piece;
            setMessages(prev => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: assistantText };
              return copy;
            });
          }
        }
      }
      if (capturedSession) setSessionId(capturedSession);
      speak(assistantText);
    } catch (e) {
      setMessages(prev => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: `We hit a snag reaching the AI service. ${e.message}` };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const toggleListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Speech recognition is not available in this browser. Please use Chrome.");
      return;
    }
    if (listening) {
      recogRef.current?.stop();
      setListening(false);
      return;
    }
    const recog = new SR();
    recogRef.current = recog;
    recog.lang = lang;
    recog.continuous = false;
    recog.interimResults = false;
    recog.onresult = (ev) => {
      const text = ev.results?.[0]?.[0]?.transcript;
      if (text) send(text);
    };
    recog.onend = () => setListening(false);
    recog.onerror = () => setListening(false);
    recog.start();
    setListening(true);
    setVoiceOn(true);
  };

  return (
    <>
      {!open && (
        <button
          data-testid={TEST_IDS.ai.launcher}
          onClick={() => setOpen(true)}
          className="fixed z-[60] bottom-24 right-6 flex items-center gap-3 pl-4 pr-5 py-3 bg-[#0B1F3A] text-[#F8F7F2] shadow-lg border border-[#D4AF37]/50 hover:bg-[#3399FF] transition"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-70 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4AF37]" />
          </span>
          <MessageCircle size={16} />
          <span className="text-[11px] uppercase tracking-[0.22em]">AI Concierge</span>
        </button>
      )}

      {open && (
        <div
          data-testid={TEST_IDS.ai.dialog}
          className="fixed z-[60] bottom-24 right-6 w-[min(420px,95vw)] h-[min(640px,85vh)] bg-white border border-[#0B1F3A]/15 shadow-2xl flex flex-col"
        >
          <div className="px-5 py-4 border-b border-[#0B1F3A]/10 flex items-center justify-between bg-[#0B1F3A] text-[#F8F7F2]">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">First Providence Capital</div>
              <div className="font-display text-lg leading-tight">AI Concierge</div>
            </div>
            <button data-testid={TEST_IDS.ai.close} onClick={() => setOpen(false)} className="text-[#F8F7F2]/70 hover:text-[#D4AF37]">
              <X size={18} />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-[#0B1F3A]/10 bg-[#F8F7F2] space-y-2">
            <div className="flex items-center gap-2">
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#5F6B7A] font-semibold">Language</div>
              <div className="flex-1 h-px bg-[#0B1F3A]/10" />
              {QUICK_LANGS.map(q => (
                <button
                  key={q.code}
                  onClick={() => switchLang(q.code)}
                  data-testid={`ai-lang-${q.code}`}
                  className={`px-2.5 py-1 text-[11px] font-medium border transition ${
                    lang === q.code
                      ? "bg-[#0B1F3A] text-[#F8F7F2] border-[#0B1F3A]"
                      : "bg-white text-[#0B1F3A] border-[#0B1F3A]/20 hover:border-[#3399FF] hover:text-[#3399FF]"
                  }`}
                >
                  {q.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                data-testid={TEST_IDS.ai.agentSelect}
                value={agent}
                onChange={(e) => { setAgent(e.target.value); setSessionId(null); }}
                className="text-xs bg-white border border-[#0B1F3A]/20 px-2 py-2 rounded-none focus:outline-none focus:border-[#3399FF]"
              >
                {AGENTS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <select
                value={lang}
                onChange={(e) => switchLang(e.target.value)}
                className="text-xs bg-white border border-[#0B1F3A]/20 px-2 py-2 rounded-none focus:outline-none focus:border-[#3399FF]"
              >
                {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-5 py-4 space-y-4 bg-[#F8F7F2]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#0B1F3A] text-[#F8F7F2]"
                    : "bg-white border border-[#0B1F3A]/10 text-[#0B1F3A]"
                }`}>
                  {m.content || (streaming && i === messages.length - 1 ? <span className="stream-caret" /> : null)}
                  {streaming && i === messages.length - 1 && m.content && <span className="stream-caret" />}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#0B1F3A]/10 px-4 py-3 bg-white">
            <div className="flex items-center gap-2">
              <button
                data-testid={TEST_IDS.ai.voiceToggle}
                onClick={toggleListen}
                title="Voice input"
                className={`h-10 w-10 flex items-center justify-center border ${listening ? "bg-[#D4AF37] text-[#0B1F3A] border-[#D4AF37]" : "border-[#0B1F3A]/20 text-[#0B1F3A] hover:bg-[#0B1F3A]/5"}`}
              >
                {listening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <button
                onClick={() => setVoiceOn(v => !v)}
                title="Read replies aloud"
                className={`h-10 w-10 flex items-center justify-center border ${voiceOn ? "bg-[#3399FF] text-white border-[#3399FF]" : "border-[#0B1F3A]/20 text-[#0B1F3A] hover:bg-[#0B1F3A]/5"}`}
              >
                <Volume2 size={16} />
              </button>
              <input
                data-testid={TEST_IDS.ai.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Ask about strategies, geographies, diligence…"
                disabled={streaming}
                className="flex-1 h-10 px-3 text-sm bg-white border border-[#0B1F3A]/20 focus:outline-none focus:border-[#3399FF]"
              />
              <button
                data-testid={TEST_IDS.ai.send}
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="h-10 px-4 bg-[#0B1F3A] text-[#F8F7F2] hover:bg-[#3399FF] disabled:opacity-40 flex items-center gap-2"
              >
                <Send size={14} />
              </button>
            </div>
            <div className="mt-2 text-[10px] text-[#5F6B7A] tracking-wide">
              Confidential. Powered by First Providence AI. Not investment advice.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
